import React, { useState } from "react";
import {
  HardDrive,
  Mail,
  ChevronLeft,
  ChevronRight,
  Database,
  FileSpreadsheet,
  Inbox,
  ArrowRight,
} from "lucide-react";
import EventBridgeSchedule from "./EventBridgeSchedule";
import EmailMonitorSchedule from "./EmailMonitorSchedule";
import OutlookMonitorSchedule from "./OutlookMonitorSchedule";
import MicrosoftOutlookIcon from "../../../icons/microsoftOutlook";

type ActiveView = "menu" | "google_drive" | "email" | "outlook";

const DataSourceManagement: React.FC = () => {
  const [activeView, setActiveView] = useState<ActiveView>("menu");

  if (activeView === "google_drive") {
    return (
      <div>
        {/* Back button */}
        <div className="px-6 pt-4 pb-2">
          <button
            onClick={() => setActiveView("menu")}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted hover:text-primary transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Data Sources
          </button>
        </div>
        <EventBridgeSchedule />
      </div>
    );
  }

  if (activeView === "email") {
    return (
      <div>
        {/* Back button */}
        <div className="px-6 pt-4 pb-2">
          <button
            onClick={() => setActiveView("menu")}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted hover:text-primary transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Data Sources
          </button>
        </div>
        <EmailMonitorSchedule />
      </div>
    );
  }

  if (activeView === "outlook") {
    return (
      <div>
        {/* Back button */}
        <div className="px-6 pt-4 pb-2">
          <button
            onClick={() => setActiveView("menu")}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted hover:text-primary transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Data Sources
          </button>
        </div>
        <OutlookMonitorSchedule />
      </div>
    );
  }

  // ── Menu view ──
  return (
    <div className="p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 bg-violet-100 rounded-xl">
              <Database className="h-6 w-6 text-violet-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                Connect to Data Source
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Choose a data source to monitor for changes
              </p>
            </div>
          </div>
        </div>

        {/* Data Source Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Google Drive Card */}
          <button
            onClick={() => setActiveView("google_drive")}
            className="group relative bg-surface-overlay rounded-2xl border border-border-default p-6 text-left hover:border-violet-300 hover:shadow-lg transition-all duration-300 overflow-hidden"
          >
            {/* Accent top border */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-violet-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            {/* Icon */}
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-50 rounded-xl group-hover:bg-blue-100 transition-colors duration-300">
                <HardDrive className="h-7 w-7 text-blue-600" />
              </div>
              <ArrowRight className="h-5 w-5 text-gray-300 group-hover:text-violet-500 group-hover:translate-x-1 transition-all duration-300" />
            </div>

            {/* Content */}
            <h3 className="text-lg font-semibold text-gray-900 mb-1.5 group-hover:text-violet-700 transition-colors">
              Connect Google Drive
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed mb-4">
              Monitor Google Drive files for changes. Get notified when spreadsheets, documents, or other files are modified.
            </p>

            {/* Features */}
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-subtle bg-surface-2 px-2.5 py-1 rounded-full border border-border-default">
                <FileSpreadsheet className="h-3 w-3" />
                Files
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-subtle bg-surface-2 px-2.5 py-1 rounded-full border border-border-default">
                <Mail className="h-3 w-3" />
                SES Alerts
              </span>
            </div>
          </button>

          {/* Email Monitor Card */}
          <button
            onClick={() => setActiveView("email")}
            className="group relative bg-surface-overlay rounded-2xl border border-border-default p-6 text-left hover:border-violet-300 hover:shadow-lg transition-all duration-300 overflow-hidden"
          >
            {/* Accent top border */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            {/* Icon */}
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-emerald-50 rounded-xl group-hover:bg-emerald-100 transition-colors duration-300">
                <Mail className="h-7 w-7 text-emerald-600" />
              </div>
              <ArrowRight className="h-5 w-5 text-gray-300 group-hover:text-violet-500 group-hover:translate-x-1 transition-all duration-300" />
            </div>

            {/* Content */}
            <h3 className="text-lg font-semibold text-gray-900 mb-1.5 group-hover:text-violet-700 transition-colors">
              Connect Email Address
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed mb-4">
              Monitor a Gmail inbox for new emails. Get notified with AI-powered summaries when new messages arrive.
            </p>

            {/* Features */}
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-subtle bg-surface-2 px-2.5 py-1 rounded-full border border-border-default">
                <Inbox className="h-3 w-3" />
                Inbox
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-subtle bg-surface-2 px-2.5 py-1 rounded-full border border-border-default">
                <Mail className="h-3 w-3" />
                SES Alerts
              </span>
            </div>
          </button>

          {/* Outlook Monitor Card */}
          <button
            onClick={() => setActiveView("outlook")}
            className="group relative bg-surface-overlay rounded-2xl border border-border-default p-6 text-left hover:border-violet-300 hover:shadow-lg transition-all duration-300 overflow-hidden"
          >
            {/* Accent top border */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-sky-500 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            {/* Icon */}
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-sky-50 rounded-xl group-hover:bg-sky-100 transition-colors duration-300">
                <MicrosoftOutlookIcon size={28} />
              </div>
              <ArrowRight className="h-5 w-5 text-gray-300 group-hover:text-violet-500 group-hover:translate-x-1 transition-all duration-300" />
            </div>

            {/* Content */}
            <h3 className="text-lg font-semibold text-gray-900 mb-1.5 group-hover:text-violet-700 transition-colors">
              Connect Outlook
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed mb-4">
              Monitor a Microsoft Outlook inbox for new emails with AI-powered summaries.
            </p>

            {/* Features */}
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-subtle bg-surface-2 px-2.5 py-1 rounded-full border border-border-default">
                <Inbox className="h-3 w-3" />
                Inbox
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-subtle bg-surface-2 px-2.5 py-1 rounded-full border border-border-default">
                <Mail className="h-3 w-3" />
                SES Alerts
              </span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataSourceManagement;
